import React from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import Login from "./pages/Login";
import AppLayout from "./components/Layout/AppLayout";
import Dashboard from "./pages/Dashboard";
import CustomersPage from "./pages/CustomersPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetail from "./pages/OrderDetail";
import OrderReports from "./pages/OrderReports";
import InventoryPage from "./pages/InventoryPage";
import AssetDetail from "./pages/AssetDetail";
import VendorsPage from "./pages/VendorsPage";
import PurchasesPage from "./pages/PurchasesPage";
import PurchaseDetail from "./components/Purchases/PurchaseDetail";
import SalesPage from "./pages/SalesPage";
import SalesDetail from "./components/Sales/SalesDetail";
import InvoicePrint from "./components/Sales/InvoicePrint";

import ProformaDetail from "./components/Proforma/ProformaDetail";
import MeterTestingForm from "./pages/MeterTestingForm";

// Import MSEDCL pages
import MSEDCLDashboard from "./pages/MSEDCLDashboard";
import WCRPage from "./pages/WCRPage";
import PaymentMilestonesPage from "./pages/PaymentMilestonesPage";
import VendorAgreementPage from "./pages/VendorAgreementPage";

import DeclarationPage from "./pages/DeclarationPage";


import ProformaPage from "./pages/Proforma/ProformaPage";
import ChannelPartnersPage from "./pages/ChannelPartnersPage";
import LeadsPage from "./pages/LeadsPage";


import "./App.css";

import ProformaGeneratorPage from "./pages/ProformaGeneratorPage";
import QuotationPage from "./pages/QuotationPage";
import UserManagementPage from "./pages/UserManagementPage";
import "./App.css";

// Placeholder components for other pages
const InvoicesPage = () => (
  <div>
    <h2>Invoices</h2>
    <p>Coming soon...</p>
  </div>
);

const PrivateRoute = ({ children, layout = true, allowedRoles }) => {
  const isAuthenticated = !!localStorage.getItem("token");
  if (!isAuthenticated) return <Navigate to="/login" />;

  if (allowedRoles) {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  }

  return layout ? <AppLayout>{children}</AppLayout> : children;
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#1890ff",
          borderRadius: 6,
        },
      }}
    >
      <AntdApp>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Main Routes */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />

            {/* User Management - Admin only */}
            <Route
              path="/user-management"
              element={
                <PrivateRoute allowedRoles={['Admin']}>
                  <UserManagementPage />
                </PrivateRoute>
              }
            />

            {/* Leads Route */}
            <Route
              path="/leads"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <LeadsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <CustomersPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/orders"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <OrdersPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/orders/:id"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <OrderDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/reports/orders"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <OrderReports />
                </PrivateRoute>
              }
            />

            <Route
              path="/inventory/assets/:id"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <AssetDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/inventory"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <InventoryPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/vendors"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <VendorsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/vendor-agreement"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <VendorAgreementPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/channel-partners"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <ChannelPartnersPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/purchases"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <PurchasesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/purchases/:id"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <PurchaseDetail />
                </PrivateRoute>
              }
            />

            {/* Sales Invoices & Proforma — Accounts role access */}
            <Route
              path="/sales"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Accounts']}>
                  <SalesPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/sales/:id"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Accounts']}>
                  <SalesDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/proforma"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Accounts']}>
                  <ProformaPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/proforma/:id"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Accounts']}>
                  <ProformaDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/sales/:id/print"
              element={
                <PrivateRoute layout={false} allowedRoles={['Admin', 'Manager', 'Accounts']}>
                  <InvoicePrint />
                </PrivateRoute>
              }
            />

            <Route
              path="/payment-milestones"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <PaymentMilestonesPage />
                </PrivateRoute>
              }
            />

            {/* MSEDCL Documents Routes */}
            <Route
              path="/msedcl"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <MSEDCLDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/msedcl/declaration"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <DeclarationPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/msedcl/wcr"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <WCRPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/msedcl/meter-testing"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <MeterTestingForm />
                </PrivateRoute>
              }
            />

            <Route
              path="/msedcl/proforma"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <ProformaGeneratorPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/quotations"
              element={
                <PrivateRoute allowedRoles={['Admin', 'Manager', 'Officer']}>
                  <QuotationPage />
                </PrivateRoute>
              }
            />

            {/* Catch-all route - MUST BE LAST */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;